import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { Card, Modal, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import './tables.css';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setSelectedArea, setSelectedTable } from '../../store/slices/tablesSlice';
import {
  useGetAreasQuery,
  useGetTablesQuery,
  useGetTablesByAreaQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useCreateTableMutation,
  useUpdateTableMutation,
  useUpdateTableStatusMutation,
  useDeleteTableMutation,
  useOpenTableMutation,
  useCloseTableMutation,
  useSplitTablesMutation,
  tablesApi,
} from '../../services/tablesApi';
import { useProcessPaymentMutation, useApplyDiscountMutation, useCancelOrderItemMutation, ordersApi } from '../../services/ordersApi';
import { useCreateTableEventMutation, createTableEventData } from '../../services/tableEventsApi';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import AreaSelector from './AreaSelector';
import TableGrid from './TableGrid';
import AreaFormModal from './modals/AreaFormModal';
import TableFormModal from './modals/TableFormModal';
import OpenTableModal from './modals/OpenTableModal';
import TableManagement from '../../pages/TableManagement';
import ComandearModal from './modals/ComandearModal';
import PaymentModal from './modals/PaymentModal';
import DiscountModal from './modals/DiscountModal';
import TransferTableModal from './modals/TransferTableModal';
import MergeTablesModal from './modals/MergeTablesModal';
import { SolicitarFacturaModal } from '../facturacion';
import { Area, Table, TableStatus, MergedTableInfo } from '../../types/tables';
import { Order } from '../../types/orders';
import { computeFloorStats, computeAreaOccupancy } from './tableHelpers';
import { useHubEvent } from '../../hooks/useHubEvent';
import { TABLES_CHANGED_EVENT, RECONNECTED_EVENT } from '../../services/realtimeHub';
import { canCancelOrderItems } from '../../utils/roleHelpers';
import { getRetryableWriteErrorMessage } from '../../utils/errorHelpers';
import { newClientId } from '../../utils/clientId';

const TablesManager: React.FC = () => {
  // Static Modal.confirm / message silently no-op under AntD v5 with <App> —
  // both must come from App.useApp() (see project convention).
  const { message, modal } = App.useApp();
  const { isMobile, width } = useResponsive();
  const dispatch = useAppDispatch();
  const { selectedAreaId, selectedTableId } = useAppSelector(state => state.tables);
  const { user } = useAppSelector(state => state.auth);

  // Page states
  const [showTableManagement, setShowTableManagement] = useState(false);
  const [managingTable, setManagingTable] = useState<Table | undefined>(undefined);
  // Lo levanta TableManagement al abrir comandear: el panel cede la pantalla completa.
  const [comandeandoInPanel, setComandeandoInPanel] = useState(false);

  // La vista partida necesita 560px de cuadrícula MÁS un panel utilizable. Por
  // debajo de esto al panel le quedaban ~170px en una tablet de 768 y los
  // botones y la lista de consumo se colapsaban: ahí el detalle toma la
  // pantalla completa, igual que en el teléfono.
  const isManaging = showTableManagement && !!managingTable;
  const canSplit = width >= 1200;

  // Clase en <body> mientras la vista partida está abierta. El <Content> del
  // layout trae margen y padding EN LÍNEA, así que desde la página no hay forma
  // de cancelarlos: el margen negativo sólo alcanza el padding (más allá,
  // `overflow: auto` recorta) y quedaba una franja muerta en los cuatro bordes.
  const splitFlush = isManaging && canSplit;
  useEffect(() => {
    document.body.classList.toggle('tables-split', splitFlush);
    return () => document.body.classList.remove('tables-split');
  }, [splitFlush]);

  // Alto de la vista partida, MEDIDO. No basta con `flex: 1`: el <Layout> de
  // arriba sólo declara min-height, así que la cadena de altos queda en `auto`,
  // el panel crece con su contenido y su scroll interno nunca se activa.
  // Tampoco sirve restar el header a mano, porque dentro del Content puede
  // haber además el aviso de caja abierta. Se mide dónde empieza la vista y se
  // toma lo que queda de ventana.
  const splitRef = useRef<HTMLDivElement | null>(null);
  const [splitHeight, setSplitHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!splitFlush) {
      setSplitHeight(null);
      return;
    }
    const measure = () => {
      const el = splitRef.current;
      if (!el) return;
      setSplitHeight(window.innerHeight - el.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener('resize', measure);
    // El aviso de caja aparece y desaparece encima de la vista sin que medie un
    // resize de ventana: hay que volver a medir cuando el Content cambia de alto.
    const parent = splitRef.current?.parentElement;
    const observer = parent ? new ResizeObserver(measure) : null;
    if (parent && observer) observer.observe(parent);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [splitFlush]);

  // Modal states
  const [areaModalVisible, setAreaModalVisible] = useState(false);
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [openTableModalVisible, setOpenTableModalVisible] = useState(false);
  const [comandearModalVisible, setComandearModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | undefined>(undefined);
  const [editingTable, setEditingTable] = useState<Table | undefined>(undefined);
  const [openingTable, setOpeningTable] = useState<Table | undefined>(undefined);
  const [comandearTable, setComandearTable] = useState<Table | undefined>(undefined);
  const [paymentTable, setPaymentTable] = useState<Table | undefined>(undefined);
  const [paymentOrder, setPaymentOrder] = useState<Order | undefined>(undefined);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountTable, setDiscountTable] = useState<Table | undefined>(undefined);
  const [discountOrder, setDiscountOrder] = useState<Order | undefined>(undefined);
  const [facturacionModalVisible, setFacturacionModalVisible] = useState(false);
  const [facturacionOrder, setFacturacionOrder] = useState<Order | undefined>(undefined);
  const [showFacturaPrompt, setShowFacturaPrompt] = useState(false);
  const [paidOrder, setPaidOrder] = useState<Order | undefined>(undefined);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferTable, setTransferTable] = useState<Table | undefined>(undefined);
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergingTable, setMergingTable] = useState<Table | undefined>(undefined);

  // API hooks
  useGetRestaurantSettingsQuery();
  useGetBoolSettingQuery('FacturacionActiva');

  const { data: areas = [], isLoading: areasLoading } = useGetAreasQuery();
  // All tables are always fetched (no skip): the floor-pulse chips and the per-area
  // occupancy counts in the pills are global, even while an area filter is active.
  // Tiempo real: el evento SignalR TablesChanged refetchea al instante cuando otro
  // mesero abre/cierra/transfiere/une una mesa; el polling de 30s es solo fallback
  // por si el websocket se cae (antes no había ninguno y la vista quedaba stale).
  const { data: allTables = [], isLoading: allTablesLoading, refetch: refetchAllTables } = useGetTablesQuery(undefined, {
    pollingInterval: 30000,
  });
  const { data: areaTables = [], isLoading: areaTablesLoading, refetch: refetchAreaTables } = useGetTablesByAreaQuery(
    selectedAreaId!,
    { skip: !selectedAreaId, pollingInterval: 30000 }
  );

  const refetchTablesRealtime = () => {
    refetchAllTables();
    if (selectedAreaId) refetchAreaTables();
  };
  useHubEvent(TABLES_CHANGED_EVENT, refetchTablesRealtime);
  useHubEvent(RECONNECTED_EVENT, refetchTablesRealtime);

  const tables = selectedAreaId ? areaTables : allTables;
  const tablesLoading = selectedAreaId ? areaTablesLoading : allTablesLoading;
  const refetchTables = selectedAreaId ? refetchAreaTables : refetchAllTables;

  const floorStats = useMemo(() => computeFloorStats(allTables), [allTables]);
  const occupancyByArea = useMemo(() => computeAreaOccupancy(allTables), [allTables]);

  // Mutations
  const [createArea, { isLoading: createAreaLoading }] = useCreateAreaMutation();
  const [updateArea, { isLoading: updateAreaLoading }] = useUpdateAreaMutation();
  useDeleteAreaMutation();
  const [createTable, { isLoading: createTableLoading }] = useCreateTableMutation();
  const [updateTable, { isLoading: updateTableLoading }] = useUpdateTableMutation();
  const [updateTableStatus] = useUpdateTableStatusMutation();
  const [deleteTable] = useDeleteTableMutation();
  const [openTable, { isLoading: openTableLoading }] = useOpenTableMutation();
  const [closeTable] = useCloseTableMutation();
  const [splitTables] = useSplitTablesMutation();
  const [processPayment] = useProcessPaymentMutation();
  const [applyDiscount] = useApplyDiscountMutation();
  const [cancelOrderItem] = useCancelOrderItemMutation();
  const [createTableEvent] = useCreateTableEventMutation();


  // No auto-select: default is "Todas" (selectedAreaId === null)

  // Area handlers
  const handleAreaChange = (areaId: string | null) => {
    dispatch(setSelectedArea(areaId));
  };

  const handleAddArea = () => {
    setEditingArea(undefined);
    setAreaModalVisible(true);
  };

  const handleAreaSubmit = async (values: { name: string; description?: string }) => {
    try {
      if (editingArea) {
        await updateArea({ id: editingArea.id, ...values }).unwrap();
      } else {
        const newArea = await createArea(values).unwrap();
        dispatch(setSelectedArea(newArea.id));
      }
      setAreaModalVisible(false);
    } catch (error) {
      message.error('Error al guardar área');
    }
  };

  // Table handlers
  const handleTableSelect = (table: Table) => {
    // A merged secondary has no order of its own — redirect to the primary,
    // where the group's account lives.
    if (table.mergedIntoTableId) {
      const primary = allTables.find(t => t.id === table.mergedIntoTableId);
      if (primary) {
        message.info(`Mesa ${table.number} está unida a la Mesa ${primary.number}`);
        handleTableSelect(primary);
      }
      return;
    }
    // If table is occupied or in precheck, show table management page
    if (table.status === 'Occupied' || table.status === 'PreCheck') {
      // Volver a tocar la mesa que ya se está viendo cierra el panel: en la
      // cuadrícula la mesa sigue ahí marcada, así que el mismo toque es el
      // gesto natural para soltarla.
      if (showTableManagement && managingTable?.id === table.id && !comandeandoInPanel) {
        handleBackToTables();
        return;
      }
      setManagingTable(table);
      setShowTableManagement(true);
    } else {
      // For other states, update Redux selection state
      if (table.id === selectedTableId) {
        dispatch(setSelectedTable(null));
      } else {
        dispatch(setSelectedTable(table.id));
      }
    }
  };

  const handleAddTable = () => {
    setEditingTable(undefined);
    setTableModalVisible(true);
  };

  const handleEditTable = (table: Table) => {
    setEditingTable(table);
    setTableModalVisible(true);
  };

  const handleTableSubmit = async (values: any) => {
    try {
      if (editingTable) {
        await updateTable({ id: editingTable.id, ...values }).unwrap();
      } else {
        await createTable(values).unwrap();
      }
      setTableModalVisible(false);
    } catch (error) {
      message.error('Error al guardar mesa');
    }
  };

  const handleDeleteTable = (tableId: string) => {
    modal.confirm({
      title: '¿Estás seguro de eliminar esta mesa?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteTable(tableId).unwrap();
          message.success('Mesa eliminada exitosamente');
          if (selectedTableId === tableId) {
            dispatch(setSelectedTable(null));
          }
        } catch (error) {
          message.error('Error al eliminar mesa');
        }
      },
    });
  };

  const handleTableStatusChange = async (tableId: string, status: TableStatus) => {
    try {
      // The backend UpdateTableStatusAsync method already creates a table event automatically,
      // so we don't need to manually create one here to avoid duplicates.
      //
      // When the request is "mark Available" we first force a fresh fetch
      // of the table to defend against stale cache: the menu items and the
      // "Liberar" button only render for status === 'Dirty' / 'Reserved',
      // but if the cache is stale a click can land while the table has
      // already been re-occupied on another device. This is exactly how
      // Banquetes D'Marisa lost order 2205 ($180) on 2026-05-07. The
      // backend has its own guard (TableStatusGuard.EnsureCanTransition)
      // that returns 409 in that case — we treat the refetch as a
      // best-effort early bail-out so the user gets a clearer message.
      if (status === 'Available') {
        // `subscribe: false` so this one-shot refetch does not register a
        // long-lived RTK Query subscriber. Without it, repeated "liberar"
        // attempts accumulate live subscriptions on the same table cache
        // entry (keepUnusedDataFor only fires once subscriber count hits
        // zero) — see PR #52 review P2.
        const fresh = await dispatch(
          tablesApi.endpoints.getTable.initiate(tableId, { forceRefetch: true, subscribe: false })
        ).unwrap();

        if (fresh.status === 'Occupied' || fresh.status === 'PreCheck') {
          message.warning(
            `Mesa ${fresh.number} ya tiene una orden activa. Cobra o cancela la orden antes de liberarla.`
          );
          return;
        }
        if (fresh.status === 'Available') {
          message.info(`Mesa ${fresh.number} ya está disponible.`);
          return;
        }
      }

      await updateTableStatus({ id: tableId, status: { status } }).unwrap();

      message.success('Estado de mesa actualizado');
    } catch (error: any) {
      // 409 = backend guard rejected (e.g. trying to mark Available with an Open order).
      // The cliente-side refetch above usually catches this first, but it covers
      // the race where the order is opened between refetch and PATCH.
      if (error?.status === 409) {
        message.error(error?.data?.message ?? 'No se puede liberar: la mesa tiene una orden activa.');
      } else {
        message.error('Error al actualizar estado de mesa');
      }
    }
  };

  const handleOpenTable = (table: Table) => {
    setOpeningTable(table);
    setOpenTableModalVisible(true);
  };

  const handleOpenTableSubmit = async (values: any) => {
    try {
      if (openingTable) {
        const previousStatus = openingTable.status;
        
        await openTable({
          tableId: openingTable.id,
          // Id de la orden desde aquí: con él, reintentar no abre la mesa dos veces, y es
          // lo que deja abrirla sin conexión (la cola manda esta misma petición después).
          orderId: newClientId(),
          customerCount: values.customerCount,
          adults: values.adults,
          children: values.children,
          waiterId: values.waiterId,
          notes: values.notes,
        }).unwrap();
        
        // Log table event
        try {
          await createTableEvent({
            tableId: openingTable.id,
            eventType: 'TableOpened',
            eventData: createTableEventData.tableOpened({
              customerCount: values.customerCount,
              adults: values.adults,
              children: values.children,
              waiterId: values.waiterId,
              waiterName: values.waiterName,
              notes: values.notes,
            }, previousStatus),
            createdBy: user?.id || 'system',
          }).unwrap();
        } catch (eventError) {
          console.error('❌ Failed to log table open event:', eventError);
          // Don't throw - the main action succeeded
        }
        
        setOpenTableModalVisible(false);
        message.success(`Mesa ${openingTable.number} abierta exitosamente`);

        // Navigate to the table management view (comandear) for the newly opened table
        const updatedTable = { ...openingTable, status: 'Occupied' as const };
        setManagingTable(updatedTable);
        setShowTableManagement(true);
        dispatch(setSelectedTable(null));
      }
    } catch (error) {
      message.error('Error al abrir mesa');
    }
  };

  const handleCloseTable = async (tableId: string) => {
    modal.confirm({
      title: '¿Cerrar esta mesa?',
      icon: <ExclamationCircleOutlined />,
      content: 'Se cerrará la orden actual de la mesa.',
      okText: 'Sí, cerrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          // Get table info before closing for event logging
          const tableToClose = tables.find(t => t.id === tableId);
          const previousStatus = tableToClose?.status || 'Unknown';
          
          await closeTable(tableId).unwrap();
          
          // Log table event
          try {
            await createTableEvent({
              tableId,
              eventType: 'TableClosed',
              eventData: createTableEventData.tableClosed({
                reason: 'Cierre normal después de pago',
                paymentMethod: 'Normal',
              }, previousStatus),
              createdBy: user?.id || 'system',
            }).unwrap();
          } catch (eventError) {
            console.error('❌ Failed to log table close event:', eventError);
            // Don't throw - the main action succeeded
          }
          
          message.success('Mesa cerrada exitosamente');
        } catch (error) {
          message.error('Error al cerrar mesa');
        }
      },
    });
  };

  const handleTransferTable = (table: Table) => {
    setTransferTable(table);
    setTransferModalVisible(true);
  };

  const handleTransferSuccess = () => {
    setTransferModalVisible(false);
    setTransferTable(undefined);
    refetchTables();
    // If we're in table management view, go back to tables view
    if (showTableManagement) {
      setShowTableManagement(false);
      setManagingTable(undefined);
    }
  };

  // Merge/split handlers
  const handleMergeTable = (table: Table) => {
    setMergingTable(table);
    setMergeModalVisible(true);
  };

  const handleMergeSuccess = (primaryTable: Table, joinedTables: MergedTableInfo[]) => {
    setMergeModalVisible(false);
    setMergingTable(undefined);
    refetchTables();
    // Typical flow: the family just arrived, the tables were joined while
    // still free — chain straight into opening the primary so the waiter
    // doesn't have to find it again in the grid. The refetch hasn't landed
    // yet, so graft the just-joined tables onto the primary by hand for the
    // combined-capacity display.
    if (primaryTable.status === 'Available' || primaryTable.status === 'Reserved') {
      setOpeningTable({
        ...primaryTable,
        mergedTables: [...(primaryTable.mergedTables || []), ...joinedTables],
      });
      setOpenTableModalVisible(true);
    }
  };

  const handleSplitTable = (table: Table) => {
    const joined = (table.mergedTables || []).map(t => t.number).join(', ');
    modal.confirm({
      title: '¿Separar las mesas?',
      icon: <ExclamationCircleOutlined />,
      content: `Mesa ${table.number} se separará de: ${joined}. Las mesas liberadas quedarán disponibles.`,
      okText: 'Sí, separar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await splitTables(table.id).unwrap();
          message.success('Mesas separadas exitosamente');
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al separar las mesas');
        }
      },
    });
  };

  // Table management handlers
  const handleBackToTables = () => {
    setShowTableManagement(false);
    setManagingTable(undefined);
    setComandeandoInPanel(false);
  };

  const handleComandear = (table: Table) => {
    setComandearTable(table);
    setComandearModalVisible(true);
  };

  const handlePrecuenta = async (table: Table, order: Order) => {
    // When called from TableManagement, execute the save logic directly
    try {
      await updateTableStatus({ id: table.id, status: { status: 'PreCheck' } }).unwrap();
      
      // Invalidate cache to refresh data
      dispatch(ordersApi.util.invalidateTags(['Order']));
      dispatch(tablesApi.util.invalidateTags(['Table']));
      
      message.success('Mesa cambiada a estado Precuenta');
    } catch (error) {
      message.error('Error al guardar precuenta');
      throw error;
    }
  };

  const handleRefreshTableData = async () => {
    try {
      // Refetch tables data to get the latest status
      const result = await refetchTables();
      
      // Update the managing table with fresh data
      if (managingTable && result.data) {
        const updatedTable = result.data.find(t => t.id === managingTable.id);
        if (updatedTable) {
          setManagingTable(updatedTable);
        }
      }
    } catch (error) {
      console.error('Error refreshing table data:', error);
    }
  };


  const handleLiquidar = (table: Table, order: Order) => {
    setPaymentTable(table);
    setPaymentOrder(order);
    setPaymentModalVisible(true);
  };

  const handleDescuentos = (table: Table, order: Order) => {
    setDiscountTable(table);
    setDiscountOrder(order);
    setDiscountModalVisible(true);
  };

  const handleForceCloseTable = async (tableId: string) => {
    try {
      // Get table info before closing for event logging
      const tableToClose = managingTable || tables.find(t => t.id === tableId);
      const previousStatus = tableToClose?.status || 'Unknown';
      
      // Close the table
      await closeTable(tableId).unwrap();
      
      // Log table event
      try {
        await createTableEvent({
          tableId,
          eventType: 'TableClosedForce',
          eventData: createTableEventData.tableClosedForce({
            reason: 'Forzado por el usuario',
            authorizedBy: user ? `${user.firstName} ${user.lastName}` : 'Sistema',
            orderTotal: 0, // Force close typically means $0 total
          }, previousStatus),
          createdBy: user?.id || 'system',
        }).unwrap();
      } catch (eventError) {
        console.error('❌ Failed to log table force close event:', eventError);
        // Don't throw - the main action succeeded
      }
      
      setShowTableManagement(false);
      setManagingTable(undefined);
      message.success('Mesa cerrada forzadamente');
    } catch (error: any) {
      console.error('Error al cerrar mesa:', error);
      message.error(error?.data?.message || 'Error al cerrar mesa');
      throw error;
    }
  };

  const handleCancelOrderItem = async (orderItemId: string, orderId?: string) => {
    try {
      if (!orderId) {
        throw new Error('Order ID is required to cancel item');
      }

      // Espejo del [Authorize] del endpoint: sin esto el usuario recibe un 403 crudo
      // después de confirmar la cancelación.
      if (!canCancelOrderItems(user?.role)) {
        message.warning('Solo un gerente o administrador puede cancelar productos de una cuenta.');
        return;
      }

      // Call the cancellation API
      await cancelOrderItem({
        orderId,
        OrderItemId: orderItemId,
        Reason: 'Cancelado por el personal',
        Notes: 'Cancelado desde la gestión de mesas',
        // AuthorizedByUserId ya no se manda: el backend toma el autorizador del token,
        // porque mandarlo desde el cliente convertía la autorización en la firma del
        // propio que cancela.
      }).unwrap();

      message.success('Producto cancelado exitosamente');
    } catch (error: any) {
      console.error('Error cancelling order item:', error);
      message.error(error?.data?.message || error?.data?.title || 'Error al cancelar producto');
      throw error;
    }
  };

  const handleReopenTable = async (tableId: string) => {
    try {
      // Get current table info for event logging
      const tableToReopen = managingTable || tables.find(t => t.id === tableId);
      const previousStatus = tableToReopen?.status || 'Unknown';
      
      await updateTableStatus({ id: tableId, status: { status: 'Occupied' } }).unwrap();
      
      // Log table event
      try {
        await createTableEvent({
          tableId,
          eventType: 'TableStatusChanged',
          eventData: createTableEventData.tableStatusChanged(
            previousStatus,
            'Occupied',
            {
              reason: 'Mesa reabierta desde precuenta',
              authorizedBy: user ? `${user.firstName} ${user.lastName}` : 'Sistema',
            }
          ),
          createdBy: user?.id || 'system',
        }).unwrap();
      } catch (eventError) {
        console.error('❌ Failed to log table reopen event:', eventError);
        // Don't throw - the main action succeeded
      }
      
      // Invalidate cache to refresh data
      dispatch(tablesApi.util.invalidateTags(['Table']));
      
      message.success('Mesa reabierta exitosamente');
    } catch (error) {
      message.error('Error al reabrir mesa');
      throw error;
    }
  };

  // Post-pago: solo housekeeping de pantalla. La mesa la pasa a Sucia el BACKEND
  // (OrderService.ProcessPaymentAsync → ITableService.MarkTableDirtyAfterPaymentAsync),
  // no este cliente: así el cobro deja la mesa igual venga de aquí, del menú de
  // órdenes o de un webhook de terminal, y una falla de red al volver del pago ya
  // no puede dejar la mesa Ocupada con la orden cerrada.
  const finalizeAfterPayment = async () => {
    dispatch(tablesApi.util.invalidateTags(['Table']));
    dispatch(ordersApi.util.invalidateTags(['Order']));

    setPaymentModalVisible(false);
    setShowTableManagement(false);
    setManagingTable(undefined);

    message.success('Pago procesado exitosamente. Mesa marcada como sucia');
  };

  // El cobro en terminal también lo cierra el backend al recibir el webhook
  // (puede llegar con este modal cerrado): mismo post-pago de pantalla.
  const handleTerminalPaymentSettled = finalizeAfterPayment;

  // Payment modal handlers
  const handleProcessPayment = async (
    payments: Array<{ id?: string; methodId: string; amount: number; reference?: string }>,
    tip: number,
    tableData?: Table,
    orderData?: Order
  ) => {
    // Use passed parameters if available, otherwise use state
    const tableToUse = tableData || paymentTable;
    const orderToUse = orderData || paymentOrder;
    
    if (!tableToUse || !orderToUse) {
      console.error('Missing table or order data for payment processing');
      return;
    }

    try {
      // El id viaja como Id del pago: con él un reintento no cobra dos veces.
      const transformedPayments = payments.map(payment => ({
        id: payment.id,
        methodId: payment.methodId,
        amount: payment.amount,
        reference: payment.reference
      }));

      // Process payment via backend API
      // Note: discountAmount and discountPercentage are set to 0 because 
      // discounts are already applied and stored in the database
      const paymentPayload = {
        orderId: orderToUse.id,
        tableId: tableToUse.id,
        payments: transformedPayments,
        tip,
        discountAmount: 0, // Discounts already applied via ApplyDiscountAsync
        discountPercentage: 0, // Discounts already applied via ApplyDiscountAsync
      };
      
      await processPayment(paymentPayload).unwrap();

      await finalizeAfterPayment();
    } catch (error: any) {
      console.error('Payment processing error:', error);
      console.error('Error details:', error?.data);
      console.error('Error status:', error?.status);

      // Se delega en getErrorMessage en vez de recorrer data.errors/message/title a
      // mano: esa escalera terminaba en el literal "Error desconocido" justo en el
      // caso más común de todos —un 401 por token vencido, que llega SIN cuerpo—, y
      // mandaba a investigar la orden en vez de la sesión. getErrorMessage cae al
      // estado HTTP cuando no hay cuerpo, así que un 401 dice "inicie sesión
      // nuevamente" y una caída de red dice que revises la conexión.
      const errorMessage = getRetryableWriteErrorMessage(error, 'No se pudo completar el cobro');

      message.error(`Error al procesar el pago: ${errorMessage}`, 8); // Show for 8 seconds
      throw error;
    }
  };

  const handlePrintReceipt = (payments: any[], tip: number, tableData?: Table, orderData?: Order) => {
    // Use passed parameters if available, otherwise use state
    const tableToUse = tableData || paymentTable;
    const orderToUse = orderData || paymentOrder;

    if (!tableToUse || !orderToUse) {
      message.error('No hay datos de pago para imprimir');
      return;
    }

    // Create a hidden iframe for printing
    const printWindow = document.createElement('iframe');
    printWindow.style.position = 'fixed';
    printWindow.style.right = '0';
    printWindow.style.bottom = '0';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = '0';
    document.body.appendChild(printWindow);

    const printDocument = printWindow.contentDocument || printWindow.contentWindow?.document;
    if (!printDocument) {
      message.error('Error al preparar la impresión');
      document.body.removeChild(printWindow);
      return;
    }

    // Import React and ReactDOM for rendering
    import('react-dom/client').then(({ createRoot }) => {
      import('../receipts/PrintReceipt').then((module) => {
        const PrintReceipt = module.default;

        // Create print content
        const printContent = document.createElement('div');
        printDocument.body.appendChild(printContent);

        // Add print styles
        const style = printDocument.createElement('style');
        style.textContent = `
          /* El iframe hereda el color-scheme del documento que lo embebe: en
             tema oscuro el texto por omisión se vuelve BLANCO y el ticket sale
             en blanco sobre el papel. El ticket es papel, siempre claro.
             Canvas/CanvasText son colores de sistema: con el esquema forzado a
             claro resuelven a blanco y negro, sin hex que el guard de tema
             tenga que perdonar. */
          html, body {
            color-scheme: light;
            background: Canvas;
            color: CanvasText;
          }
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
            @page {
              margin: 0;
              size: 80mm auto;
            }
          }
        `;
        printDocument.head.appendChild(style);

        // Render the receipt
        const root = createRoot(printContent);
        root.render(
          React.createElement(PrintReceipt, {
            table: tableToUse,
            order: orderToUse,
            payments,
            tip,
            discountAmount,
            discountPercentage,
          })
        );

        // Wait for rendering and then print
        setTimeout(() => {
          printWindow.contentWindow?.print();

          // Clean up after printing
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        }, 500);
      }).catch((error) => {
        console.error('Error loading PrintReceipt component:', error);
        message.error('Error al cargar el componente de impresión');
        document.body.removeChild(printWindow);
      });
    }).catch((error) => {
      console.error('Error loading react-dom/client:', error);
      message.error('Error al inicializar impresión');
      document.body.removeChild(printWindow);
    });
  };


  // Discount modal handlers
  const handleApplyDiscountToOrder = async (discountData: {
    type: 'percentage' | 'amount';
    value: number;
    reason: string;
    notes?: string;
    authorizedBy: string;
    orderId?: string; // Make orderId optional parameter
  }) => {
    // Use orderId from parameter if provided, otherwise fall back to discountOrder state
    const orderIdToUse = discountData.orderId || discountOrder?.id;
    
    if (!orderIdToUse) {
      return;
    }

    try {
      const apiPayload = {
        orderId: orderIdToUse,
        type: discountData.type,
        value: discountData.value,
        reason: discountData.reason,
        notes: discountData.notes,
        authorizedBy: discountData.authorizedBy,
      };
      
      await applyDiscount(apiPayload).unwrap();

      // Update local discount state for payment calculations
      if (discountData.type === 'percentage') {
        setDiscountPercentage(discountData.value);
        setDiscountAmount(0);
      } else {
        setDiscountAmount(discountData.value);
        setDiscountPercentage(0);
      }

      setDiscountModalVisible(false);
      // El "aplicado exitosamente" lo canta DiscountModal al volver de aquí; decirlo
      // también en este lado sacaba el mismo toast dos veces.

      // Invalidate cache to refresh data and show updated discount in table menu
      dispatch(ordersApi.util.invalidateTags(['Order']));
      dispatch(tablesApi.util.invalidateTags(['Table']));
      
      // Don't automatically open payment modal - stay in table menu to show applied discount
    } catch (error) {
      // No se reporta aquí: DiscountModal pinta el mensaje del backend al recibir el
      // throw, y avisar en los dos lados sacaba dos toasts, el útil y el genérico.
      throw error;
    }
  };

  const selectedArea = areas.find(a => a.id === selectedAreaId);

  // Panel lateral: la mesa abierta se atiende junto a la cuadrícula, que se
  // queda a la vista.
  const sidePanelMode = splitFlush;
  // La cuadrícula se retira cuando el detalle manda: sin espacio para partir, o
  // comandeando (elegir productos necesita todo el ancho).
  const gridHidden = isManaging && (!canSplit || comandeandoInPanel);

  const tableManagementPanel = showTableManagement && managingTable ? (
    <TableManagement
      table={managingTable}
      onBack={handleBackToTables}
      onComandear={handleComandear}
      onPrecuenta={handlePrecuenta}
      onLiquidar={handleLiquidar}
      onDescuentos={handleDescuentos}
      onCloseTable={handleForceCloseTable}
      onCancelOrderItem={handleCancelOrderItem}
      onReopenTable={handleReopenTable}
      onTransferTable={handleTransferTable}
      onTableUpdate={handleRefreshTableData}
      onProcessPayment={handleProcessPayment}
      onPrintReceipt={handlePrintReceipt}
      onTerminalPaymentSettled={handleTerminalPaymentSettled}
      onApplyDiscountToOrder={handleApplyDiscountToOrder}
      embedded={canSplit}
      onComandearVisibleChange={setComandeandoInPanel}
    />
  ) : null;

  const tablesCard = (
    <Card
      style={{
        borderRadius: isMobile ? 8 : 12,
        padding: 0
      }}
      styles={{
        body: {
          padding: isMobile ? '12px' : '24px'
        }
      }}
    >
      <AreaSelector
        areas={areas}
        selectedAreaId={selectedAreaId}
        onAreaChange={handleAreaChange}
        onAddArea={handleAddArea}
        loading={areasLoading}
        occupancyByArea={occupancyByArea}
        totalTables={allTables.length}
      />

      <TableGrid
        tables={tables}
        areaName={selectedArea?.name || 'Todas las mesas'}
        floorStats={floorStats}
        selectedTableId={sidePanelMode ? managingTable!.id : selectedTableId}
        onTableSelect={handleTableSelect}
        onTableEdit={handleEditTable}
        onTableDelete={handleDeleteTable}
        onTableOpen={handleOpenTable}
        onTableClose={handleCloseTable}
        onTableTransfer={handleTransferTable}
        onTableMerge={handleMergeTable}
        onTableSplit={handleSplitTable}
        onTableStatusChange={handleTableStatusChange}
        onAddTable={handleAddTable}
        hasAreas={areas.length > 0}
        loading={tablesLoading}
        showAreaColumn={selectedAreaId === null}
      />
    </Card>
  );

  return (
    <div
      ref={splitRef}
      className={`tables-container${sidePanelMode ? ' tables-container--split' : ''}`}
      style={sidePanelMode ? {
        // body.tables-split ya dejó el <Content> a ras. El alto va en píxeles
        // medidos: un alto definido es lo que permite que el panel acote su
        // scroll interno en vez de crecer con el contenido.
        padding: 12,
        margin: 0,
        height: splitHeight ?? undefined,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      } : { padding: isMobile ? '12px' : '24px' }}
    >
      {/* UN SOLO árbol para las tres vistas (cuadrícula sola, partida, y detalle
          a pantalla completa): lo que cambia son los estilos, nunca la posición.
          Si el panel cambiara de lugar React lo remontaría y tiraría el carrito
          de comandear a medio capturar — pasaba al cruzar los 768px. Por lo
          mismo la cuadrícula se OCULTA en vez de desmontarse: desmontarla
          reseteaba a "Tarjetas" la vista de lista que el usuario eligió. */}
      <div style={sidePanelMode ? {
        display: 'flex',
        gap: gridHidden ? 0 : 16,
        flex: 1,
        minHeight: 0,
      } : undefined}>
        <div
          className={sidePanelMode ? 'table-panel__scroll' : undefined}
          style={gridHidden ? { display: 'none' } : (sidePanelMode ? {
            // Ancho fijo de dos tarjetas: la tarjeta no debe cambiar de tamaño
            // al abrir el panel. Todo lo que sobra es del panel.
            flex: '0 0 560px',
            minWidth: 0,
            height: '100%',
            overflowY: 'auto',
          } : undefined)}
        >
          {tablesCard}
        </div>

        {isManaging && (
          <div style={sidePanelMode ? { flex: 1, minWidth: 0, minHeight: 0 } : undefined}>
            {tableManagementPanel}
          </div>
        )}
      </div>

      {/* Modals */}
      <AreaFormModal
        visible={areaModalVisible}
        onCancel={() => setAreaModalVisible(false)}
        onSubmit={handleAreaSubmit}
        area={editingArea}
        loading={createAreaLoading || updateAreaLoading}
      />

      <TableFormModal
        visible={tableModalVisible}
        onCancel={() => setTableModalVisible(false)}
        onSubmit={handleTableSubmit}
        table={editingTable}
        areas={areas}
        selectedAreaId={selectedAreaId}
        loading={createTableLoading || updateTableLoading}
      />

      <OpenTableModal
        visible={openTableModalVisible}
        onCancel={() => setOpenTableModalVisible(false)}
        onSubmit={handleOpenTableSubmit}
        table={openingTable}
        loading={openTableLoading}
      />


      <ComandearModal
        visible={comandearModalVisible}
        onCancel={() => setComandearModalVisible(false)}
        table={comandearTable}
      />

      {/* La precuenta se monta en TableManagement (el menú de la mesa) y en la lista de
          órdenes. Aquí había un tercer montaje que nunca se abría —su `visible` solo se
          ponía en false y sus table/order eran useState sin setter, siempre undefined. */}

      <PaymentModal
        visible={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        table={paymentTable}
        order={paymentOrder}
        discountAmount={discountAmount}
        discountPercentage={discountPercentage}
        onProcessPayment={handleProcessPayment}
        onPrintReceipt={handlePrintReceipt}
        terminalTarget={paymentOrder ? { orderId: paymentOrder.id, tableId: paymentTable?.id } : undefined}
        onTerminalPaymentSettled={() => handleTerminalPaymentSettled()}
      />

      <DiscountModal
        visible={discountModalVisible}
        onCancel={() => setDiscountModalVisible(false)}
        table={discountTable}
        order={discountOrder}
        onApplyDiscount={handleApplyDiscountToOrder}
      />

      {/* Invoice Prompt Modal */}
      <Modal
        title="Pago Procesado Exitosamente"
        open={showFacturaPrompt}
        onOk={() => {
          setShowFacturaPrompt(false);
          setFacturacionOrder(paidOrder);
          setFacturacionModalVisible(true);
        }}
        onCancel={() => {
          setShowFacturaPrompt(false);
          setPaidOrder(undefined);
          message.success('Pago procesado exitosamente. Mesa marcada como sucia');
        }}
        okText="Solicitar Factura"
        cancelText="No, Gracias"
        width={500}
      >
        <p>¿Desea solicitar una factura para esta orden?</p>
      </Modal>

      {/* Invoice Request Modal */}
      <SolicitarFacturaModal
        visible={facturacionModalVisible}
        onCancel={() => {
          setFacturacionModalVisible(false);
          setFacturacionOrder(undefined);
          setPaidOrder(undefined);
        }}
        order={facturacionOrder}
        onSuccess={(factura) => {
          message.success(`Factura ${factura.folio} generada exitosamente`);
          setFacturacionModalVisible(false);
          setFacturacionOrder(undefined);
          setPaidOrder(undefined);
        }}
      />

      {/* Transfer Table Modal */}
      <TransferTableModal
        visible={transferModalVisible}
        sourceTable={transferTable}
        onCancel={() => {
          setTransferModalVisible(false);
          setTransferTable(undefined);
        }}
        onSuccess={handleTransferSuccess}
      />

      {/* Merge Tables Modal */}
      <MergeTablesModal
        visible={mergeModalVisible}
        primaryTable={mergingTable}
        onCancel={() => {
          setMergeModalVisible(false);
          setMergingTable(undefined);
        }}
        onSuccess={handleMergeSuccess}
      />
    </div>
  );
};

export default TablesManager;